/**
 * Buchungen und Zustaende in Worten -- fuer Historie und Sync-Protokoll.
 *
 * KONZEPT.md §15.4 gibt den Ton vor: „Deine Buchung ‚nach GB-07' von 14:12 Uhr
 * wurde durch eine neuere Buchung ersetzt. Aktueller Stand: im Einsatz --
 * Pumpenstelle Nord, gebucht 15:41 Uhr." Kurze Halbsaetze, Kennungen statt
 * Kennnummern, Uhrzeit statt Zeitstempel.
 *
 * Rein: Die Namen kommen herein, nichts wird nachgeladen.
 */

import type {
  Buchung,
  BuchungsArt,
  Einsatzstelle,
  Lagerort,
  StatusArt,
  Verbleib,
  Zustand,
} from "@thw/contracts";

/** Wo ein Artikel ist, in Worten (ADR 0005). Fest, nicht einstellbar. */
export const VERBLEIB_TEXT: Record<Verbleib, string> = {
  nicht_erfasst: "noch nicht erfasst",
  im_lager: "im Lager",
  im_einsatz: "im Einsatz",
  ausgeliehen: "ausgeliehen",
};

/** Die Buchungsarten als Wort fuer die Historie. */
export const BUCHUNGSART_TEXT: Record<BuchungsArt, string> = {
  einbuchen: "eingebucht",
  entnehmen: "entnommen",
  einsatz: "in den Einsatz",
  ausgabe: "ausgegeben",
  ruecknahme: "zurückgenommen",
  umlagerung: "verschoben",
  statusaenderung: "Status geändert",
  korrektur: "Korrektur",
};

/** Was zum Benennen gebraucht wird -- alles aus der lokalen Kopie. */
export interface Namen {
  lagerorte: ReadonlyMap<string, Lagerort>;
  einsatzstellen: ReadonlyMap<string, Einsatzstelle>;
  statusArten: readonly StatusArt[];
}

function ortName(id: string | null, namen: Namen): string {
  if (id === null) return "—";
  return namen.lagerorte.get(id)?.kennung ?? "unbekannter Lagerort";
}

function statusName(schluessel: string, namen: Namen): string {
  return namen.statusArten.find((s) => s.schluessel === schluessel)?.bezeichnung ?? schluessel;
}

/**
 * Was eine Buchung bewirken sollte, als Halbsatz: „nach GB-07",
 * „in den Einsatz — Pumpenstelle Nord", „Status: defekt".
 */
export function buchungKurz(b: Buchung, namen: Namen): string {
  switch (b.art) {
    case "einbuchen":
    case "umlagerung":
    case "ruecknahme":
      return `nach ${ortName(b.ziel_lagerort_id, namen)}`;
    case "einsatz": {
      const stelle =
        b.einsatzstelle_id === null ? null : namen.einsatzstellen.get(b.einsatzstelle_id);
      return stelle === undefined || stelle === null
        ? "in den Einsatz"
        : `in den Einsatz — ${stelle.bezeichnung}`;
    }
    case "entnehmen":
      return "entnommen";
    case "ausgabe":
      return "ausgegeben";
    case "statusaenderung":
      return b.status === null ? "Status geändert" : `Status: ${statusName(b.status, namen)}`;
    case "korrektur": {
      const teile: string[] = [];
      if (b.ziel_lagerort_id !== null) teile.push(`nach ${ortName(b.ziel_lagerort_id, namen)}`);
      if (b.status !== null) teile.push(`Status: ${statusName(b.status, namen)}`);
      if (b.menge_absolut !== null) teile.push(`Bestand ${String(b.menge_absolut)}`);
      return `Korrektur${teile.length > 0 ? ` (${teile.join(", ")})` : ""}`;
    }
  }
}

/**
 * Eine Buchung fuer die Historie: „verschoben nach GB-07“, „Status: defekt“.
 *
 * Eine abgeleitete Buchung (Inhalt eines Packstuecks) zeigt auf das Packstueck
 * selbst (ADR 0012). Fuer die Historie des Einzelteils zaehlt aber, was mit dem
 * Packstueck geschah: „mit GB-01 verschoben nach LKW-1“. Dazu braucht es die
 * ausloesende Buchung, falls sie lokal bekannt ist.
 */
export function buchungFuerHistorie(b: Buchung, namen: Namen, ausloeserin?: Buchung): string {
  const eigen = (x: Buchung): string =>
    x.art === "einbuchen" || x.art === "umlagerung" || x.art === "ruecknahme"
      ? `${BUCHUNGSART_TEXT[x.art]} ${buchungKurz(x, namen)}`
      : buchungKurz(x, namen);

  if (b.abgeleitet_von === null) return eigen(b);

  // Eine Statusaenderung kommt nur mit, wenn die Kiste verloren ging -- oder
  // wieder da ist (ADR 0016). Sie traegt ihren eigenen Status: den
  // archivierenden oder den Standard.
  if (b.art === "statusaenderung") {
    let kiste: Lagerort | undefined;
    for (const l of namen.lagerorte.values()) {
      if (ausloeserin !== undefined && l.artikel_id === ausloeserin.artikel_id) kiste = l;
    }
    return `${buchungKurz(b, namen)} — mit ${kiste?.kennung ?? "dem Packstück"}`;
  }

  const packstueck =
    b.ziel_lagerort_id === null ? "dem Packstück" : ortName(b.ziel_lagerort_id, namen);
  return ausloeserin === undefined
    ? `mit ${packstueck} ${BUCHUNGSART_TEXT[b.art]}`
    : `mit ${packstueck} ${eigen(ausloeserin)}`;
}

/**
 * Der Stand eines Artikels in einer Zeile: „im Einsatz — Pumpenstelle Nord",
 * „im Lager — GB-07, defekt".
 *
 * Der Status erscheint nur, wenn er nicht der gewoehnliche ist -- „im Lager,
 * einsatzbereit" sagt nichts, was man nicht ohnehin annimmt.
 */
export function standKurz(z: Zustand, namen: Namen): string {
  let ort = VERBLEIB_TEXT[z.verbleib];
  if (z.verbleib === "im_lager" && z.lagerort_id !== null) {
    const lagerort = namen.lagerorte.get(z.lagerort_id);
    ort += ` — ${lagerort === undefined ? "unbekannter Lagerort" : `${lagerort.kennung} ${lagerort.bezeichnung}`}`;
  }
  if (z.verbleib === "im_einsatz" && z.einsatzstelle_id !== null) {
    const stelle = namen.einsatzstellen.get(z.einsatzstelle_id);
    if (stelle !== undefined) ort += ` — ${stelle.bezeichnung}`;
  }
  // Draussen, aber im Packstueck (ADR 0012).
  if (z.verbleib !== "im_lager" && z.lagerort_id !== null) {
    ort += `, in ${namen.lagerorte.get(z.lagerort_id)?.kennung ?? "einem Packstück"}`;
  }
  const standard = namen.statusArten.find((s) => s.standard)?.schluessel;
  return z.status === standard ? ort : `${ort}, ${statusName(z.status, namen)}`;
}

/**
 * Eine Uhrzeit, wie man sie sagt: „14:12 Uhr" -- und mit Datum, wenn es nicht
 * heute war.
 *
 * `jetzt` kommt herein, damit die Funktion rein bleibt und sich pruefen laesst.
 */
export function uhrzeit(iso: string, jetzt: Date = new Date()): string {
  const d = new Date(iso);
  const zeit = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  const gleicherTag = d.toDateString() === jetzt.toDateString();
  if (gleicherTag) return `${zeit} Uhr`;
  const tag = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  return `${tag}, ${zeit} Uhr`;
}

/**
 * Wie lange etwas her ist, kurz genug für die Kopfleiste: „gerade eben“,
 * „vor 2 Min.“, „vor 3 Std.“, sonst das Datum (Rückmeldungen 7.2).
 */
export function vorWieLange(iso: string, jetzt: Date = new Date()): string {
  const minuten = Math.floor((jetzt.getTime() - Date.parse(iso)) / 60_000);
  if (minuten < 1) return "gerade eben";
  if (minuten < 60) return `vor ${String(minuten)} Min.`;
  const stunden = Math.floor(minuten / 60);
  if (stunden < 24) return `vor ${String(stunden)} Std.`;
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}
